import { useState, useEffect } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useMachineStore, INITIAL_STATE } from 'Store/Machine'
import { toast } from 'react-toastify'
import { shallow } from 'zustand/shallow'
import * as Yup from 'yup'

const ViewModel = () => {
  const validationSchema = Yup.object().shape({
    brand: Yup.string().required('Required'),
    machineCode: Yup.string().required('Required'),
    machineModel: Yup.string().required('Required'),
    machineTypeID: Yup.number().not([0]).required('Required'),
  })

  const navigate = useNavigate()
  const { id } = useParams()
  const location = useLocation()
  const isCreate = location.pathname.includes('create')

  const { types, selected, getOne, getTypes, create, edit, clearState } = useMachineStore(
    state => ({
      types: state.types,
      selected: state.selected,
      getOne: state.getOne,
      getTypes: state.getTypes,
      create: state.create,
      edit: state.edit,
      clearState: state.clearState,
    }),
    shallow
  )

  const [formState, setFormState] = useState(INITIAL_STATE)
  const [formError, setFormError] = useState<any>(
    Object.keys(INITIAL_STATE).reduce((acc, key) => ({ ...acc, [key]: false }), {})
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const typeOptions = types.map((type: any) => ({ value: type.id, label: type.title }))

  const fetchData = () => {
    getTypes()

    if (!isCreate && id) {
      setIsLoading(true)
      getOne(id).then(({ data, success }) => {
        if (!success) {
          toast.error('เกิดข้อผิดพลาดในการดึงข้อมูล : ' + data)
        } else {
          setFormState(data)
          setIsLoading(false)
        }
      })
    }
  }

  const onChangeFormState = (name: string, value: any) => {
    setFormState(prev => ({ ...prev, [name]: value }))
  }

  const onCancel = () => {
    navigate(-1)
  }

  const validateBeforeSubmit = () => {
    return validationSchema
      .validate(formState, { abortEarly: false })
      .then(() => {
        return true
      })
      .catch(err => {
        const errors: { [key: string]: any } = {}
        err.inner.forEach((e: any) => {
          errors[e.path] = e.message
        })
        setFormError(errors)

        return false
      })
  }

  const handleSubmitCreate = () => {
    create(formState).then(({ success, data }) => {
      if (!success) {
        toast.error('สร้างเครื่องจักรไม่สำเร็จ : ' + data)
        setIsSubmitting(false)
      } else {
        toast.success('สร้างเครื่องจักรสำเร็จ')
        navigate(-1)
      }
    })
  }

  const handleSubmitEdit = () => {
    const body: { [key: string]: any } = {}
    const editableFields = ['brand', 'machineCode', 'machineModel', 'machineTypeID', 'pillingType']

    editableFields.forEach(field => {
      // @ts-ignore
      if (formState[field] !== selected[field]) {
        // @ts-ignore
        body[field] = formState[field]
      }
    })

    if (Object.keys(body).length === 0) {
      setIsSubmitting(false)
      return
    }

    edit(Number(id), body).then(({ success, data }) => {
      if (!success) {
        toast.error('แก้ไขเครื่องจักรไม่สำเร็จ : ' + data)
        setIsSubmitting(false)
      } else {
        setIsSubmitting(false)
        toast.success('แก้ไขเครื่องจักรสำเร็จ')
        fetchData()
      }
    })
  }

  const onSubmit = async () => {
    setIsSubmitting(true)

    const validatedResult = await validateBeforeSubmit()
    if (!validatedResult) {
      setIsSubmitting(false)
      return
    }

    if (isCreate) {
      handleSubmitCreate()
    } else if (!isCreate && id) {
      handleSubmitEdit()
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line
  }, [id])

  useEffect(() => {
    return () => {
      clearState()
    }
    // eslint-disable-next-line
  }, [])

  return {
    typeOptions,
    isLoading,
    isSubmitting,
    isCreate,
    formState,
    formError,
    onChangeFormState,
    onSubmit,
    onCancel,
  }
}

export default ViewModel
